// ─────────────────────────────────────────────────────────────────────────────
// PERMANENT NOTE — DO NOT REMOVE (even after the _self→_top override is gone)
// ─────────────────────────────────────────────────────────────────────────────
// Link button `target` values have THREE distinct meanings; mixing them up
// silently breaks either LMS embeds or SCORM/ContentSpace player sessions:
//
//   _blank — new tab/window. Always safe for external URLs.
//   _top   — top-level window. Replaces the whole browser tab. In SCORM/LMS
//            the player iframe is unloaded — SCORM session ends. Use when
//            the intent is "leave the course" (e.g. SSO redirect, another
//            portal, a different product).
//   _self  — current browsing context. Inside an iframe this replaces the
//            INNER iframe only; the LMS/player shell stays. Rarely useful
//            for author-written links — almost always a bug when the course
//            runs inside ContentSpace player or an LMS iframe.
//
// Any future UI that lets authors pick a target MUST expose all three with
// clear labels, not a yes/no toggle. Conscious choice, not a default.
// ─────────────────────────────────────────────────────────────────────────────
import type { ButtonAction, ButtonElementValue } from '@vedalib/editor/lib/elements'
import {
  ButtonActionType,
  ButtonBlockAction,
  ButtonContinueAction,
  ButtonNavigationAction,
} from '@vedalib/editor/lib/elements'
import * as R from 'ramda'
import { prepareLinkUrl } from 'utils'

import { BlockMode, SectionTypeEnum } from 'services/Store/Project/enums'
import type { ProjectActionsTypes } from 'services/Store/Project/reducer'
import type { MapType, Section } from 'services/Store/Project/types'
import type { IBlockState } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import { isTypeGuardAction } from './typeGuard'

const ALL_ACTIONS_OPTIONS: { value: ButtonBlockAction; label: string }[] = [
  { value: ButtonBlockAction.testStart, label: t('input.option.testStart') },
  { value: ButtonBlockAction.validate, label: t('input.option.validate') },
  { value: ButtonBlockAction.testEnd, label: t('input.option.testEnd') },
  { value: ButtonBlockAction.testRestart, label: t('input.option.testRestart') },
  { value: ButtonBlockAction.courseStart, label: t('input.option.courseStart') },
  { value: ButtonBlockAction.courseEnd, label: t('input.option.courseEnd') },
  { value: ButtonBlockAction.reset, label: t('input.option.reset') },
]

export const navigationActionOptions: { value: ButtonNavigationAction; label: string }[] = [
  { value: ButtonNavigationAction.nextSection, label: t('input.option.nextSection') },
  { value: ButtonNavigationAction.prevSection, label: t('input.option.prevSection') },
  { value: ButtonNavigationAction.custom, label: t('input.option.custom') },
]

const actionOptions: Record<ButtonActionType, { value: ButtonActionType; label: string }> = {
  [ButtonActionType.blockAction]: {
    value: ButtonActionType.blockAction,
    label: t('input.option.blockAction'),
  },
  [ButtonActionType.navigation]: {
    value: ButtonActionType.navigation,
    label: t('input.option.courseNavigation'),
  },
  [ButtonActionType.link]: { value: ButtonActionType.link, label: t('input.option.link') },
  [ButtonActionType.file]: { value: ButtonActionType.file, label: t('input.option.downloadFile') },
  [ButtonActionType.continue]: {
    value: ButtonActionType.continue,
    label: t('input.option.continue'),
  },
  [ButtonActionType.decision]: {
    value: ButtonActionType.decision,
    label: t('input.option.decision'),
  },
}

export const valueByType = {
  [ButtonActionType.navigation]: {
    actionType: ButtonActionType.navigation,
    action: ButtonNavigationAction.nextSection,
    label: t('elements.button.value.nextSection'),
  },
  [ButtonActionType.link]: {
    actionType: ButtonActionType.link,
    label: t('elements.button.value.link'),
  },
  [ButtonActionType.file]: {
    actionType: ButtonActionType.file,
    label: t('elements.button.value.file'),
  },
  [ButtonActionType.continue]: {
    actionType: ButtonActionType.continue,
    action: ButtonContinueAction.none,
    label: t('elements.button.value.continue'),
  },
  [ButtonActionType.decision]: {
    actionType: ButtonActionType.decision,
    label: t('elements.button.value.decision.label'),
  },
}

export const valueByAction = {
  [ButtonBlockAction.courseStart]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.courseStart,
    label: t('elements.button.value.courseStart.label'),
    secondLabel: t('elements.button.value.courseStart.second'),
  },
  [ButtonBlockAction.validate]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.validate,
    label: t('elements.button.value.validate.label'),
    secondLabel: t('elements.button.value.validate.second'),
    thirdLabel: t('elements.button.value.validate.third'),
    fourthLabel: t('elements.button.value.validate.fourth'),
  },
  [ButtonBlockAction.testStart]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.testStart,
    label: t('elements.button.value.testStart.label'),
  },
  [ButtonBlockAction.testEnd]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.testEnd,
    label: t('elements.button.value.testEnd.label'),
    secondLabel: t('elements.button.value.testEnd.second'),
    thirdLabel: t('elements.button.value.testEnd.third'),
  },
  [ButtonBlockAction.testRestart]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.testRestart,
    label: t('elements.button.value.testRestart.label'),
  },
  [ButtonBlockAction.courseEnd]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.courseEnd,
    label: t('elements.button.value.courseEnd.label'),
  },
  [ButtonBlockAction.reset]: {
    actionType: ButtonActionType.blockAction,
    action: ButtonBlockAction.reset,
    label: t('elements.button.value.reset.label'),
  },
  [ButtonNavigationAction.nextSection]: {
    actionType: ButtonActionType.navigation,
    action: ButtonNavigationAction.nextSection,
    label: t('elements.button.value.nextSection.label'),
  },
  [ButtonNavigationAction.prevSection]: {
    actionType: ButtonActionType.navigation,
    action: ButtonNavigationAction.prevSection,
    label: t('elements.button.value.prevSection.label'),
  },
  [ButtonNavigationAction.custom]: {
    actionType: ButtonActionType.navigation,
    action: ButtonNavigationAction.custom,
    label: t('elements.button.value.custom.label'),
  },
}

export const getLastViewedSection = (section: Section[], viewedBlocks: MapType<IBlockState>) => {
  return [...section]
    .reverse()
    .find((section) => section.blocksOrder.some((bO) => viewedBlocks[bO]?.viewed))
}

interface IFilterByBlockAction {
  mode?: BlockMode
  sectionType?: SectionTypeEnum
}

const ACTIONS_IN_MODE: {
  [key in SectionTypeEnum]?: Partial<Record<BlockMode, ButtonBlockAction[]>>
} = {
  [SectionTypeEnum.test]: {
    [BlockMode.start]: [ButtonBlockAction.testStart],
    [BlockMode.end]: [ButtonBlockAction.testEnd, ButtonBlockAction.testRestart],
    [BlockMode.questions]: [
      ButtonBlockAction.validate,
      ButtonBlockAction.testEnd,
      ButtonBlockAction.reset,
    ],
  },
  [SectionTypeEnum.cover]: {
    [BlockMode.cover]: [ButtonBlockAction.courseStart],
  },
  [SectionTypeEnum.landing]: {
    [BlockMode.questions]: [ButtonBlockAction.validate],
    [BlockMode.view]: [ButtonBlockAction.courseEnd],
  },
  [SectionTypeEnum.chapter]: {},
}

const DEFAULT_ACTIONS = [
  ButtonActionType.blockAction,
  ButtonActionType.navigation,
  ButtonActionType.link,
  ButtonActionType.file,
]

export const ALLOWED_ACTIONS_IN_MODE: Partial<
  Record<SectionTypeEnum, Partial<Record<BlockMode, ButtonActionType[]>>>
> = {
  [SectionTypeEnum.test]: {
    [BlockMode.view]: [...DEFAULT_ACTIONS, ButtonActionType.continue],
    [BlockMode.end]: [...DEFAULT_ACTIONS, ButtonActionType.decision],
  },
  [SectionTypeEnum.landing]: {
    [BlockMode.questions]: [
      ...DEFAULT_ACTIONS,
      ButtonActionType.continue,
      ButtonActionType.decision,
    ],
    [BlockMode.view]: [...DEFAULT_ACTIONS, ButtonActionType.continue],
  },
}

export const getButtonActionTypesByMode = (type?: SectionTypeEnum, mode?: BlockMode) => {
  const options = (type && mode && ALLOWED_ACTIONS_IN_MODE[type]?.[mode]) || DEFAULT_ACTIONS
  return mode ? options.map((actionType) => actionOptions[actionType]) : []
}

export const filterByBlockAction = ({ mode, sectionType }: IFilterByBlockAction) => {
  const availableActions = sectionType && mode ? ACTIONS_IN_MODE[sectionType]?.[mode] : null

  if (!availableActions) {
    return []
  }

  return ALL_ACTIONS_OPTIONS.filter((option) => availableActions.includes(option.value))
}

interface IFIlterByNavigation {
  mode?: BlockMode
}

export const filterByNavigation = ({ mode }: IFIlterByNavigation) => {
  const options = navigationActionOptions

  return mode === 'cover' ? options.filter((elem) => elem.value !== 'prevSection') : options
}

export const hasLabel = (action: ButtonElementValue['action'], label: string) =>
  isTypeGuardAction(action) && R.has(label, valueByAction[action])

export const getButtonLabel = (
  map: Partial<Record<ProjectActionsTypes[keyof ProjectActionsTypes] | 'noop', string>>,
  action: ProjectActionsTypes[keyof ProjectActionsTypes] | 'noop',
) => map[action]

export interface ILabelVisibilityParams {
  action?: ButtonAction
  isProMode: boolean
  blockMode?: BlockMode
  sectionType?: SectionTypeEnum
  nextSection: boolean
  isNextBlockQuestions: boolean
}

export interface ILabelVisibility {
  secondLabel: boolean
  thirdLabel: boolean
  fourthLabel: boolean
}

// TODO: maybe better to separate logic by every labels?
export const getLabelVisibility = (params: ILabelVisibilityParams): ILabelVisibility => {
  const { action, isProMode, blockMode, sectionType, nextSection, isNextBlockQuestions } = params
  const secondLabelByAction: Partial<Record<string, boolean>> = {
    [ButtonBlockAction.courseStart]: true,
    [ButtonBlockAction.testEnd]: isProMode || (blockMode === BlockMode.end && nextSection),
    [ButtonBlockAction.validate]:
      isProMode ||
      (sectionType === SectionTypeEnum.test &&
        ((blockMode === BlockMode.questions && !isNextBlockQuestions) ||
          blockMode !== BlockMode.end)),
  }

  const thirdLabelByAction: Partial<Record<string, boolean>> = {
    [ButtonBlockAction.testEnd]: isProMode || (blockMode === BlockMode.end && !nextSection),
    [ButtonBlockAction.validate]: isProMode || sectionType === SectionTypeEnum.landing,
  }

  const fourthLabelByAction: Partial<Record<string, boolean>> = {
    [ButtonBlockAction.validate]:
      isProMode || (sectionType === SectionTypeEnum.test && !isNextBlockQuestions),
  }

  return {
    secondLabel: Boolean(action && secondLabelByAction[action]),
    thirdLabel: Boolean(action && thirdLabelByAction[action]),
    fourthLabel: Boolean(action && fourthLabelByAction[action]),
  }
}

export const getCorrectLink = (
  actionData: ButtonElementValue['actionData'],
  isEditorMode = false,
) => {
  const url = actionData && 'url' in actionData ? actionData?.url : undefined
  const rawTarget = (actionData && 'target' in actionData && actionData?.target) || '_blank'
  // TODO [2026-05-22]: Phase 1 — any persisted `_self` is rendered as `_top`.
  // Editor UI still writes `_self` (yes/no toggle). If no author/QA complains
  // by 2026-05-22, Phase 2: run data migration `actionData.target _self → _top`
  // across all projects, drop this override, and keep `_self` only if a new
  // 3-option UI (blank/self/top) is requested. Rationale + semantics: see
  // permanent header comment at the top of this file — do not delete.
  // Techdebt: docs/techdebt/2026-04-22-button-target-self-to-top.md
  const target = rawTarget === '_self' ? '_top' : rawTarget
  const correctUrl = !isEditorMode && url ? prepareLinkUrl(url) : undefined
  return { url: correctUrl, target }
}
